// Processor Log (QOR staff): the calls the API made to the processors, newest first, filtered by time (US Eastern),
// processor, kind (the calls that change something by default: lookups outnumber them a hundred to one), operation,
// result and text. What is shown was masked before it was stored: credentials, card numbers and
// personal data read ****. A row opens to the call in two parts, Request and Response, each with its first line, its
// headers and its body.
import { api, providerKey, providerName } from '@qor/web-shared';
import { useQuery } from '@tanstack/react-query';
import { type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../components/ConsoleLayout';
import { toQuery, useFilterForm, useQueryParams } from '../components/filters';
import { Pagination } from '../components/ui';

interface ProcessorCall {
  id: string;
  occurredAt: string;
  provider: string;
  operation: string;
  method: string;
  url: string;
  status: number | null;
  durationMs: number;
  error: string | null;
  requestHeaders: string | null;
  requestBody: string | null;
  responseHeaders: string | null;
  responseBody: string | null;
}
interface ProcessorLog {
  items: ProcessorCall[];
  totalCount: number;
  page: number;
  pageSize: number;
  providers: string[];
  operations: string[];
  kind: 'actions' | 'lookups' | 'all';
  hiddenLookups: number;
  from: string;
  to: string;
}

// Times are shown and filtered in US Eastern time (the client's request): America/New_York, so EST in winter and EDT
// in summer, each row saying which.
const EASTERN = 'America/New_York';
const easternParts = (at: Date) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  }).formatToParts(at);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return {
    wall: `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}:${part('second')}`,
    zone: part('timeZoneName'),
  };
};

/** "2026-10-06 05:55:25 EDT" (Eastern, to the second: calls of one action are seconds apart). */
export const easternSeconds = (iso: string) => {
  const { wall, zone } = easternParts(new Date(iso));
  return `${wall.replace('T', ' ')} ${zone}`;
};

/** A datetime-local value ("2026-10-06T09:55") read as Eastern time, as the instant it is (ISO, UTC). */
export const fromEastern = (local: string | null) => {
  if (local === null) return null;
  const wanted = Date.parse(`${local.length === 16 ? `${local}:00` : local}Z`);
  if (Number.isNaN(wanted)) return local; // the back end answers what is wrong with it
  // the zone's offset at that moment: guessed from the wall time read as UTC, then corrected once (a DST change)
  let instant = wanted;
  for (let pass = 0; pass < 2; pass++) {
    instant += wanted - Date.parse(`${easternParts(new Date(instant)).wall}Z`);
  }
  return new Date(instant).toISOString().replace('.000Z', 'Z');
};

export const succeeded = (call: Pick<ProcessorCall, 'status'>) =>
  call.status !== null && call.status >= 200 && call.status <= 299;

export function ProcessorLogPage() {
  usePageTitle('Processor Log');
  const q = useQueryParams();
  const f = {
    from: q.get('from'),
    to: q.get('to'),
    provider: q.get('provider'),
    kind: q.get('kind'),
    operation: q.get('operation'),
    result: q.get('result'),
    search: q.get('search'),
  };
  const page = q.page();
  const submit = useFilterForm('/processor-log');
  const applyNow = (e: ChangeEvent<HTMLSelectElement>) => e.currentTarget.form?.requestSubmit();
  const results = useQuery({
    queryKey: ['processor-log', f, page],
    queryFn: async (): Promise<ProcessorLog> => {
      const log = await api<Omit<ProcessorLog, 'totalCount'> & { total: number }>(
        'GET',
        `/admin/processor-calls${toQuery({ from: fromEastern(f.from), to: fromEastern(f.to), provider: providerKey(f.provider), kind: f.kind, operation: f.operation, result: f.result, search: f.search, page })}`,
      );
      return {
        ...log,
        totalCount: log.total,
        items: log.items.map((c) => ({ ...c, provider: providerName(c.provider) })),
        providers: log.providers.map(providerName),
      };
    },
  });
  const data = results.data;
  const filtered = Object.values(f).some((value) => value !== null);

  return (
    <>
      <div className="qc-page-heading">
        <h1>Processor Log</h1>
        <p className="qc-breadcrumb">
          QOR Console <span aria-hidden="true">&rsaquo;</span> <strong>Processor Log</strong>
        </p>
      </div>
      <p className="qc-breadcrumb" style={{ marginBottom: 12 }}>
        Every call to a processor, newest first. Times are US Eastern (EST/EDT); without dates, the last 24 hours.
        Credentials, card numbers and personal data were replaced with **** before the call was stored.
      </p>

      <form className="qc-cardholder-filters" onSubmit={submit} key={toQuery(f)}>
        <input type="datetime-local" name="from" defaultValue={f.from ?? ''} aria-label="From (Eastern time)" />
        <input type="datetime-local" name="to" defaultValue={f.to ?? ''} aria-label="To (Eastern time)" />
        <select name="provider" aria-label="Filter by processor" defaultValue={f.provider ?? ''} onChange={applyNow}>
          <option value="">All processors</option>
          {(data?.providers ?? (f.provider ? [f.provider] : [])).map((provider) => (
            <option key={provider} value={provider}>
              {provider}
            </option>
          ))}
        </select>
        <select name="kind" aria-label="Filter by kind of call" defaultValue={f.kind ?? ''} onChange={applyNow}>
          <option value="">Actions only</option>
          <option value="all">All calls</option>
          <option value="lookups">Lookups only</option>
        </select>
        <select name="operation" aria-label="Filter by operation" defaultValue={f.operation ?? ''} onChange={applyNow}>
          <option value="">All operations</option>
          {(data?.operations ?? (f.operation ? [f.operation] : [])).map((operation) => (
            <option key={operation} value={operation}>
              {operation}
            </option>
          ))}
        </select>
        <select name="result" aria-label="Filter by result" defaultValue={f.result ?? ''} onChange={applyNow}>
          <option value="">All results</option>
          <option value="ok">Succeeded</option>
          <option value="failed">Failed</option>
        </select>
        <input
          type="search"
          name="search"
          defaultValue={f.search ?? ''}
          placeholder="Search operation, URL, request or response..."
          aria-label="Search"
          autoComplete="off"
        />
        <button type="submit" className="qor-button qor-button-primary">
          Filter
        </button>
        {filtered && (
          <Link className="qc-button-ghost" to="/processor-log">
            Clear
          </Link>
        )}
      </form>

      {data && data.kind === 'actions' && data.hiddenLookups > 0 && (
        <p className="qc-breadcrumb" role="note" style={{ marginBottom: 12 }}>
          Showing the calls that change something (loads, activations, cards issued…). {data.hiddenLookups} lookup
          {data.hiddenLookups === 1 ? '' : 's'} (balances, statuses, histories) in this period{' '}
          {data.hiddenLookups === 1 ? 'is' : 'are'} hidden.{' '}
          <Link
            to={`/processor-log${toQuery({ from: f.from, to: f.to, provider: f.provider, kind: 'all', result: f.result, search: f.search })}`}
          >
            Show all calls
          </Link>
        </p>
      )}

      <div className="qc-panel">
        {results.isError ? (
          <p className="qc-empty">The processor log could not be read.</p>
        ) : !data ? null : data.items.length === 0 ? (
          <p className="qc-empty">
            No processor calls in this period. Calls are recorded only while the API runs with ProcessorLog__Enabled set
            to true.
          </p>
        ) : (
          <>
            <div className="qc-table-scroll">
              <table className="qc-plain-table">
                <thead>
                  <tr>
                    <th>When (Eastern)</th>
                    <th>Processor</th>
                    <th>Operation</th>
                    <th>Result</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((call) => (
                    <tr key={call.id}>
                      <td style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>{easternSeconds(call.occurredAt)}</td>
                      <td style={{ verticalAlign: 'top' }}>{call.provider}</td>
                      <td>
                        <details>
                          <summary style={{ cursor: 'pointer' }}>
                            <code className="qc-code-chip">{call.operation}</code>
                          </summary>
                          <CallPart
                            title="Request"
                            line={`${call.method} ${call.url}`}
                            headers={call.requestHeaders}
                            body={call.requestBody}
                          />
                          <CallPart
                            title="Response"
                            line={
                              call.status === null
                                ? `No answer: ${call.error ?? 'the call failed'}`
                                : `HTTP ${call.status} in ${call.durationMs} ms`
                            }
                            headers={call.responseHeaders}
                            body={call.status === null ? null : call.responseBody}
                          />
                        </details>
                      </td>
                      <td style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                        <span className={`qc-badge ${succeeded(call) ? 'qc-badge-success' : 'qc-badge-warning'}`}>
                          {call.status ?? 'No answer'}
                        </span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>{call.durationMs} ms</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={data.page}
              totalPages={Math.max(1, Math.ceil(data.totalCount / data.pageSize))}
              totalCount={data.totalCount}
              href={(p) =>
                `/processor-log${toQuery({ from: f.from, to: f.to, provider: f.provider, kind: f.kind, operation: f.operation, result: f.result, search: f.search, page: p })}`
              }
            />
          </>
        )}
      </div>
    </>
  );
}

/** One side of a call: what was sent or what came back, as a first line, the headers and the body. */
function CallPart(props: { title: 'Request' | 'Response'; line: string; headers: string | null; body: string | null }) {
  return (
    <section className="qc-log-part" aria-label={props.title}>
      <h4>{props.title}</h4>
      <code className="qc-code-chip qc-code-chip-muted">{props.line}</code>
      <h5>Headers</h5>
      <pre className="qc-log-body" aria-label={`${props.title} headers`}>
        {props.headers ?? '(none recorded)'}
      </pre>
      <h5>Body</h5>
      <pre className="qc-log-body" aria-label={`${props.title} body`}>
        {props.body ?? '(no body)'}
      </pre>
    </section>
  );
}
